import { useMemo, useState } from "react";
import { Card, Row, Col, Typography, Divider, Alert, Button } from "antd";
import { DollarCircleOutlined, TrophyOutlined } from "@ant-design/icons";
import AppInputDropdown from "../../../../components/AppInputDropdown";
import { InventoryAuditModel } from "../../../../../infrastructure/model/InventoryAuditModel";
import { getSummaryInventoryAuditUseCase } from "../../../../../applications/useCase/ApiUseCase/InventoryAuditApiUseCase";
import usePageState from "../../../../hooks/usePageState";
import useAsyncEffect from "../../../../hooks/useAsyncEffect";
import { getProfitLossDataUseCase } from "../../../../../applications/useCase/ApiUseCase/ProfitLossApiUseCase";
import { ProfitLossModel } from "../../../../../infrastructure/model/ProfitLossModel";
import { currencyFormat } from "../../../../../applications/utils/NumberUtils";

const { Title, Text } = Typography;

function ProfitLossDashboard() {
  const { isLoading, setLoading, showError, showClientError } = usePageState();

  const [inventoryAudits, setInventoryAudits] = useState<InventoryAuditModel[]>([]);
  const [profitLossData, setProfitLossData] = useState<ProfitLossModel>();

  const [startAudit, setStartAudit] = useState<number>();
  const [endAudit, setEndAudit] = useState<number>();

  async function getSummaryInventoryAudit() {
    const dataRes = await getSummaryInventoryAuditUseCase({
      setLoading,
      showError,
    });
    setInventoryAudits(dataRes);
  }

  async function getProfitLossData() {
    if (!startAudit || !endAudit) return;
    const startAuditData = inventoryAudits.find((s) => s.id === startAudit);
    const endAuditData = inventoryAudits.find((s) => s.id === endAudit);
    if (!startAuditData || !endAuditData) return;
    if (startAuditData?.auditDate > endAuditData.auditDate) {
      showClientError("Kì bắt đầu phải là khoảng thời gian trước của kì kết thúc");
      return;
    }
    const dataRes = await getProfitLossDataUseCase({
      request: {
        start_audit_id: startAudit,
        end_audit_id: endAudit,
      },
      setLoading,
      showError,
    });
    setProfitLossData(dataRes);
  }

  const endAuditInventoryOptions = useMemo(() => {
    if (startAudit === 0) return [];
    const startAuditDate = inventoryAudits?.find((f) => f.id === startAudit)?.auditDate ?? 0;
    return inventoryAudits.filter((e) => e.auditDate > startAuditDate);
  }, [startAudit]);

  useAsyncEffect(async () => {
    await getSummaryInventoryAudit();
  }, []);

  useAsyncEffect(async () => {
    if (!startAudit || !endAudit) {
      return;
    }

    await getProfitLossData();
  }, [startAudit, endAudit]);

  return (
    <div style={{ padding: 24, background: "#0f1320", minHeight: "100vh" }}>
      {inventoryAudits.length <= 1 && (
        <Card
          style={{
            // background: "#111827",
            borderRadius: 14,
            // border: "1px solid #1f2937",
            boxShadow: "0 4px 20px rgba(0,0,0,0.35)",
            marginBottom: 24,
            padding: 8,
            background: "#0f172a",
            border: "1px solid #374151",
            color: "#e5e7eb",
          }}
        >
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Alert
                type="warning"
                showIcon
                message="Không thể tính lãi lỗ"
                description="Cần ít nhất 2 kỳ kiểm kê liên tiếp để tính toán lãi/lỗ (kỳ bắt đầu và kỳ kết thúc)."
              />
            </Col>
          </Row>
        </Card>
      )}
      {/* HEADER */}
      <Row justify="space-between" align="middle" className="gap-2" style={{ marginBottom: 20 }}>
        <Col span={11}>
          <Title level={4} style={{ color: "#ffa940", margin: 0 }}>
            Báo cáo Lãi / Lỗ
          </Title>
          <Card
            style={{
              borderRadius: 14,
              boxShadow: "0 4px 20px rgba(0,0,0,0.35)",
              marginBottom: 24,
              padding: 8,
              background: "#0f172a",
              border: "1px solid #374151",
              color: "#e5e7eb",
            }}
          >
            <Row gutter={24}>
              <Col span={12}>
                <AppInputDropdown
                  orientation="col"
                  label="Kỳ bắt đầu"
                  options={inventoryAudits?.map((inven) => ({
                    label: inven.note,
                    value: inven.id,
                  }))}
                  allowClear
                  currentValue={startAudit}
                  disabled={inventoryAudits.length <= 1}
                  onChangeValue={(e) => {
                    setStartAudit(e);
                    setEndAudit(0);
                  }}
                />
              </Col>

              <Col span={12}>
                <AppInputDropdown
                  orientation="col"
                  label="Kỳ kết thúc"
                  options={endAuditInventoryOptions?.map((inven) => ({
                    label: inven.note,
                    value: inven.id,
                  }))}
                  allowClear
                  disabled={startAudit === 0 || inventoryAudits.length <= 1}
                  currentValue={endAudit}
                  onChangeValue={(e) => setEndAudit(e)}
                />
              </Col>
            </Row>
            <Button className="mt-3" variant="solid" color="primary" onClick={getProfitLossData}>
              Làm mới
            </Button>
          </Card>
        </Col>
        <Col span={12}>
          <Card
            style={{
              marginBottom: 24,
              marginTop: 24,
              padding: 8,
              background: "#13223a",
              borderRadius: 12,
              border: "1px solid #2a3c55",
            }}
          >
            <Text style={{ color: "#ccc" }}>📐 Công thức tính:</Text>

            <div style={{ marginTop: 10 }}>
              <Text style={{ color: "#52c41a" }}>Lãi/Lỗ</Text>
              <Text style={{ color: "#ccc" }}> = Doanh thu – </Text>

              <Text style={{ color: "#40a9ff" }}>(Tồn đầu kỳ + Nhập kỳ – Tồn cuối kỳ)</Text>

              <Text style={{ color: "#ccc" }}> – </Text>

              <Text style={{ color: "#ff4d4f" }}>Chi phí vận hành</Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* MAIN PROFIT CARD */}
      <Card
        style={{
          marginBottom: 20,
          background: "#141a2e",
          borderRadius: 12,
        }}
      >
        <Title level={5} style={{ color: "#fff" }}>
          <DollarCircleOutlined style={{ marginRight: 8 }} />
          Doanh thu ( Tính từ ngày{" "}
          <span>{new Date(profitLossData?.period_from ?? 0).toLocaleDateString("vi-VN")}</span> đến
          ngày <span>{new Date(profitLossData?.period_to ?? 0).toLocaleDateString("vi-VN")}</span>)
        </Title>

        <Divider style={{ borderColor: "#2a2f45" }} />

        <Row justify="space-between">
          <Text style={{ color: "#aaa" }}>– Tồn đầu kỳ (kỳ kiểm kê trước)</Text>
          <Text style={{ color: "#40a9ff" }}>
            {currencyFormat(profitLossData?.opening_inventory_cost ?? 0)}
          </Text>
        </Row>

        <Row justify="space-between">
          <Text style={{ color: "#aaa" }}>– Tổng nhập kho kỳ này</Text>
          <Text style={{ color: "#40a9ff" }}>
            {currencyFormat(profitLossData?.total_purchase_cost ?? 0)}
          </Text>
        </Row>

        <Row justify="space-between">
          <Text style={{ color: "#aaa" }}>– Tồn cuối kỳ</Text>
          <Text style={{ color: "#40a9ff" }}>
            {currencyFormat(profitLossData?.end_inventory_cost ?? 0)}
          </Text>
        </Row>

        <Divider style={{ borderColor: "#2a2f45" }} />

        <Row justify="space-between">
          <Text style={{ color: "#ccc" }}>= Giá vốn hàng bán (COGS)</Text>
          <Text style={{ color: "#ff4d4f" }}>({currencyFormat(profitLossData?.cogs ?? 0)} )</Text>
        </Row>

        <Row justify="space-between">
          <Text style={{ color: "#ccc" }}>= Lợi nhuận gộp</Text>
          <Text style={{ color: "#ff4d4f" }}>
            ({currencyFormat(profitLossData?.gross_profit ?? 0)} )
          </Text>
        </Row>

        <Row justify="space-between">
          <Text style={{ color: "#ccc" }}>– Chi phí vận hành</Text>
          <Text style={{ color: "#ff4d4f" }}>({profitLossData?.total_operating_cost ?? 0} )</Text>
        </Row>

        <Divider style={{ borderColor: "#2a2f45" }} />

        <Row justify="space-between">
          <Title level={5} style={{ color: "#ffd666" }}>
            <TrophyOutlined /> LỢI NHUẬN RÒNG
          </Title>

          <Title level={4} style={{ color: "#ff4d4f" }}>
            ({currencyFormat(profitLossData?.net_profit ?? 0)} )
          </Title>
        </Row>
      </Card>

      {/* BOTTOM CARDS */}
      <Row gutter={20}>
        {/* COST CARD */}
        <Col span={12}>
          <Card
            style={{
              background: "#141a2e",
              borderRadius: 12,
            }}
          >
            <Title level={5} style={{ color: "#ffa940" }}>
              Chi phí vận hành
            </Title>

            {profitLossData?.operating_cost?.map((item) => (
              <Row key={item.id} justify="space-between" style={{ marginBottom: 8 }}>
                <Text style={{ color: "#ccc" }}>{item.costType}</Text>
                <Text style={{ color: "#ff4d4f" }}>{currencyFormat(item.amount ?? 0)} </Text>
              </Row>
            ))}

            <Divider />

            <Row justify="space-between">
              <Text strong style={{ color: "#fff" }}>
                Tổng
              </Text>
              <Text strong style={{ color: "#ff4d4f" }}>
                {currencyFormat(profitLossData?.total_operating_cost ?? 0)}
              </Text>
            </Row>
          </Card>
        </Col>

        {/* ORDER CARD */}
        <Col span={12}>
          <Card
            style={{
              background: "#141a2e",
              borderRadius: 12,
            }}
          >
            <Title level={5} style={{ color: "#ffa940" }}>
              Đơn hàng hoàn thành
            </Title>

            <Row justify="space-between">
              <Text style={{ color: "#ccc" }}>Số đơn hoàn thành</Text>
              <Text style={{ color: "#52c41a" }}>
                {(profitLossData?.completed_order_count ?? 0)?.toLocaleString()} đơn
              </Text>
            </Row>

            <Divider />

            <Row justify="space-between">
              <Text strong style={{ color: "#fff" }}>
                Tổng doanh thu
              </Text>

              <Text strong style={{ color: "#52c41a" }}>
                {currencyFormat(profitLossData?.revenue ?? 0)}
              </Text>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default ProfitLossDashboard;
